import { contrastRatio, luminance, readableOn, type BrandingOutput, type BrandMode } from '@garage/shared';

/**
 * Thème de marque (docs/DESIGN.md, « Couleur de marque » et « Présence de la marque »).
 *
 * Les couleurs viennent des réglages du garage, jamais du code. `brandTheme` calcule, pour le mode
 * choisi (`discreet`, `affirmed`, `immersive`), les variables CSS de rôle : chaque texte ou icône posé
 * sur une couleur de marque est choisi ou ajusté (teinte conservée, clarté déplacée) pour atteindre le
 * contraste AA. `applyBrand` les pose sur `<html>` ; `brandScopeProps` sur un conteneur (aperçus).
 *
 * Rôles :
 * - `--brand-primary` / `--brand-on-primary` : couleur principale (identité) et texte posé dessus ;
 * - `--brand-accent` / `--brand-on-accent` : couleur des actions (bouton principal, compteurs, élément
 *   actif, onglet actif) : la principale en mode discret, la 2e couleur en modes affirmé et immersif ;
 * - `--brand-strong` : l'accent lisible en texte, icône ou focus sur les surfaces (≥ 4,5:1) ;
 * - `--chrome-*` : barre latérale et navigation basse ; `--masthead-*` : en-tête (téléphone, portail) ;
 * - `--mark` / `--on-mark` : monogramme du garage ; `--brand-tertiary` : 3e couleur (filets, détails).
 */
export type Brand = Pick<BrandingOutput, 'displayName' | 'primaryColor' | 'secondaryColor' | 'logoUrl'> & {
  tertiaryColor?: string | null | undefined;
  brandMode?: BrandMode | undefined;
};

export type { BrandMode };
export const BRAND_MODES: readonly BrandMode[] = ['discreet', 'affirmed', 'immersive'];

/** Contraste minimal : texte courant (AA) et éléments graphiques (indicateurs, contours). */
export const AA_TEXT = 4.5;
export const AA_UI = 3;

/** Surfaces claires de la plateforme (mêmes valeurs que `shared/styles.css`). */
export const LIGHT = { surface: '#ffffff', canvas: '#f7f6f3', sunken: '#f0eeea', ink: '#1b1a17' } as const;

type Rgb = [number, number, number];

function parseHex(hex: string): Rgb {
  const h = hex.replace('#', '');
  const full =
    h.length === 3
      ? h
          .split('')
          .map((c) => c + c)
          .join('')
      : h.slice(0, 6);
  return [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16)) as Rgb;
}

function toHex([r, g, b]: Rgb): string {
  return `#${[r, g, b]
    .map((v) =>
      Math.round(Math.min(255, Math.max(0, v)))
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`;
}

function channel(v: number): number {
  const c = v / 255;
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

function unchannel(c: number): number {
  const v = c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055;
  return v * 255;
}

// Contraste WCAG : même calcul que l'API (e-mails, icônes), dans `@garage/shared` (`color.ts`).
// `strongOn` reste ici : la version de l'interface garde la teinte (OKLCH, `adjustToContrast`) au lieu
// d'assombrir par pas ; elle est vérifiée par `theme.test.ts` pour chaque mode.
export { contrastRatio, luminance, readableOn };

/** Mélange sRGB : `t` = part de `b` (0 → `a`, 1 → `b`). */
export function mix(a: string, b: string, t: number): string {
  const x = parseHex(a);
  const y = parseHex(b);
  return toHex([0, 1, 2].map((i) => x[i]! + (y[i]! - x[i]!) * t) as Rgb);
}

// ─── OKLCH : déplacer la clarté en gardant la teinte ────────────────────────

type Lch = { l: number; c: number; h: number };

export function toOklch(hex: string): Lch {
  const [r, g, b] = parseHex(hex).map(channel) as Rgb;
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const A = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const B = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;
  return { l: L, c: Math.hypot(A, B), h: ((Math.atan2(B, A) * 180) / Math.PI + 360) % 360 };
}

function lchToLinear({ l: L, c, h }: Lch): Rgb {
  const A = c * Math.cos((h * Math.PI) / 180);
  const B = c * Math.sin((h * Math.PI) / 180);
  const l = (L + 0.3963377774 * A + 0.2158037573 * B) ** 3;
  const m = (L - 0.1055613458 * A - 0.0638541728 * B) ** 3;
  const s = (L - 0.0894841775 * A - 1.291485548 * B) ** 3;
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
}

/** Couleur OKLCH → hexadécimal, chroma réduite jusqu'à entrer dans le gamut sRGB. */
export function fromOklch(lch: Lch): string {
  const l = Math.min(1, Math.max(0, lch.l));
  let c = lch.c;
  for (let i = 0; i < 24; i++) {
    const rgb = lchToLinear({ l, c, h: lch.h });
    if (rgb.every((v) => v >= -0.0005 && v <= 1.0005)) break;
    c *= 0.85;
  }
  return toHex(lchToLinear({ l, c, h: lch.h }).map((v) => unchannel(Math.min(1, Math.max(0, v)))) as Rgb);
}

/**
 * Variante de `hex` qui atteint `min` de contraste sur `bg` : même teinte, clarté déplacée vers le
 * sombre (fond clair) ou vers le clair (fond sombre), au plus près de la couleur d'origine.
 */
export function adjustToContrast(hex: string, bg: string, min: number): string {
  if (contrastRatio(hex, bg) >= min) return hex.toLowerCase();
  const lch = toOklch(hex);
  const darker = luminance(bg) > 0.18;
  let lo = darker ? 0 : lch.l;
  let hi = darker ? lch.l : 1;
  let best = darker ? '#000000' : '#ffffff';
  for (let i = 0; i < 28; i++) {
    const mid = (lo + hi) / 2;
    const candidate = fromOklch({ ...lch, l: mid });
    const ok = contrastRatio(candidate, bg) >= min;
    if (ok) best = candidate;
    // On cherche la clarté la plus proche de l'origine qui passe.
    if (darker === ok) lo = mid;
    else hi = mid;
  }
  return contrastRatio(best, bg) >= min ? best : readableOn(bg);
}

/** Variante de la marque lisible sur fond clair (compatibilité : assombrie jusqu'à `min`). */
export function strongOn(hex: string, surface: string = LIGHT.surface, min = AA_TEXT): string {
  return adjustToContrast(hex, surface, min);
}

/**
 * Texte posé sur une couleur : la première couleur préférée (ex. la couleur principale du garage sur
 * sa 2e couleur) si elle atteint 4,5:1, sinon le noir ou le blanc le plus contrasté.
 */
export function inkOn(bg: string, preferred: (string | null | undefined)[] = []): string {
  for (const p of preferred) if (p && contrastRatio(p, bg) >= AA_TEXT) return p.toLowerCase();
  return readableOn(bg);
}

/** Pire contraste d'une couleur sur plusieurs fonds (contrôle « sur chaque surface »). */
function worst(hex: string, bgs: string[]): number {
  return Math.min(...bgs.map((b) => contrastRatio(hex, b)));
}

/** Atteint `min` sur chacun des fonds (on ajuste contre le plus exigeant, puis on vérifie). */
function legibleOnAll(hex: string, bgs: string[], min: number): string {
  let out = hex;
  for (let i = 0; i < 3 && worst(out, bgs) < min; i++) {
    const hardest = bgs.reduce((a, b) => (contrastRatio(out, a) <= contrastRatio(out, b) ? a : b));
    out = adjustToContrast(out, hardest, min + 0.05);
  }
  return out;
}

// ─── Thème sombre immersif : neutres teintés par la couleur principale ─────

/** Clartés OKLCH des neutres sombres (n-0 = surface des cartes … n-950 = texte le plus clair). */
const DARK_L: Record<string, number> = {
  '--n-0': 0.225,
  '--n-25': 0.255,
  '--n-50': 0.175,
  '--n-100': 0.15,
  '--n-150': 0.285,
  '--n-200': 0.32,
  '--n-300': 0.41,
  '--n-400': 0.57,
  '--n-500': 0.75,
  '--n-600': 0.84,
  '--n-700': 0.89,
  '--n-800': 0.93,
  '--n-900': 0.965,
  '--n-950': 0.985,
};

/** Teinte des neutres sombres : celle de la couleur principale, chroma plafonnée (fond, pas décor). */
function darkNeutrals(primary: string): Record<string, string> {
  const p = toOklch(primary);
  const c = Math.min(p.c, 0.032);
  const out: Record<string, string> = {};
  for (const [k, l] of Object.entries(DARK_L)) out[k] = fromOklch({ l, c: l > 0.5 ? c * 0.35 : c, h: p.h });
  return out;
}

// ─── Calcul du thème ────────────────────────────────────────────────────────

export interface BrandTheme {
  mode: BrandMode;
  /** Variables CSS à poser (valeurs hexadécimales calculées). */
  vars: Record<string, string>;
}

const HEX = /^#[0-9a-fA-F]{6}$/;
export const isHex = (v: string | null | undefined): v is string => !!v && HEX.test(v);

/** Normalise un mode inconnu ou absent (anciennes données, portail sans le champ) en `discreet`. */
export function brandModeOf(v: string | null | undefined): BrandMode {
  return v === 'affirmed' || v === 'immersive' ? v : 'discreet';
}

/** Barre latérale / navigation basse posée sur `chrome` : textes, filets, état actif. */
function chromeVars(chrome: string, accent: string, tertiary: string | null, primary: string): Record<string, string> {
  const on = inkOn(chrome);
  const on2 = adjustToContrast(mix(on, chrome, 0.26), chrome, AA_TEXT);
  const on3 = adjustToContrast(mix(on, chrome, 0.38), chrome, AA_TEXT);
  const line = tertiary && contrastRatio(tertiary, chrome) >= 1.6 ? mix(chrome, tertiary, 0.34) : mix(chrome, on, 0.16);
  // Élément actif : la 2e couleur pleine ; si elle se confond avec le fond, variante ajustée (≥ 3:1).
  const active = adjustToContrast(accent, chrome, AA_UI);
  // Élément actif sobre (menu de gauche, barre basse) : fond légèrement teinté de l'accent, texte de
  // la barre (≥ 4,5:1), icône et filet à l'accent (≥ 3:1 sur ce fond).
  const soft = mix(chrome, active, 0.16);
  return {
    '--chrome': chrome,
    '--on-chrome': on,
    '--on-chrome-2': on2,
    '--on-chrome-3': on3,
    '--chrome-line': line,
    '--chrome-hover': mix(chrome, on, 0.09),
    '--chrome-active': active,
    '--chrome-active-ink': inkOn(active, [primary]),
    '--chrome-active-icon': inkOn(active, [primary]),
    '--chrome-active-soft': soft,
    '--chrome-active-soft-ink': adjustToContrast(on, soft, AA_TEXT),
    '--chrome-active-soft-icon': adjustToContrast(active, soft, AA_UI),
    '--chrome-focus': on,
    '--chrome-badge': active,
    '--on-chrome-badge': inkOn(active, [primary]),
    // Sur l'entrée active (déjà à l'accent), le compteur s'inverse.
    '--chrome-badge-active': inkOn(active, [primary]),
    '--on-chrome-badge-active': active,
  };
}

/** Thème complet d'un garage pour son mode de présence. */
export function brandTheme(brand: Brand): BrandTheme {
  const mode = brandModeOf(brand.brandMode);
  const primary = brand.primaryColor.toLowerCase();
  const secondary = brand.secondaryColor.toLowerCase();
  const tertiary = isHex(brand.tertiaryColor) ? brand.tertiaryColor.toLowerCase() : null;
  const onPrimary = readableOn(primary);
  const vars: Record<string, string> = {
    '--brand-primary': primary,
    '--brand-secondary': secondary,
    '--brand-on-primary': onPrimary,
    '--brand-on-secondary': inkOn(secondary, [primary]),
  };

  if (mode === 'discreet') {
    // Interface neutre ; la couleur principale en accent (comportement historique).
    const strong = legibleOnAll(primary, [LIGHT.surface, LIGHT.canvas], AA_TEXT);
    Object.assign(vars, {
      '--brand-accent': primary,
      '--brand-on-accent': onPrimary,
      '--brand-strong': strong,
      '--brand-tertiary': tertiary ?? mix(LIGHT.surface, LIGHT.ink, 0.2),
      '--mark': primary,
      '--on-mark': onPrimary,
    });
    return { mode, vars };
  }

  if (mode === 'affirmed') {
    const surfaces = [LIGHT.surface, LIGHT.canvas];
    const strong = legibleOnAll(secondary, surfaces, AA_TEXT);
    const chrome = chromeVars(primary, secondary, tertiary, primary);
    Object.assign(vars, chrome, {
      '--brand-accent': secondary,
      '--brand-on-accent': inkOn(secondary, [primary]),
      '--brand-strong': strong,
      '--brand-tertiary': tertiary ?? mix(LIGHT.surface, LIGHT.ink, 0.2),
      '--masthead': primary,
      '--on-masthead': chrome['--on-chrome']!,
      '--on-masthead-2': chrome['--on-chrome-2']!,
      '--masthead-line': tertiary ?? secondary,
      '--mark': secondary,
      '--on-mark': inkOn(secondary, [primary]),
    });
    // 3e couleur : filets et contours des cartes et des champs, adoucis sur le blanc.
    if (tertiary) {
      vars['--line'] = mix(LIGHT.surface, tertiary, 0.42);
      vars['--line-strong'] = mix(LIGHT.surface, tertiary, 0.78);
    }
    return { mode, vars };
  }

  // Immersif : thème sombre complet, teinté par la couleur principale.
  const n = darkNeutrals(primary);
  const surface = n['--n-0']!;
  const canvas = n['--n-50']!;
  const raised = n['--n-25']!;
  const sunken = n['--n-100']!;
  // Fond de la barre : la couleur principale si elle est déjà profonde, sinon sa teinte assombrie.
  const p = toOklch(primary);
  const deep = p.l <= 0.3 ? primary : fromOklch({ l: 0.2, c: Math.min(p.c, 0.07), h: p.h });
  const chrome = toOklch(deep).l < toOklch(canvas).l ? deep : fromOklch({ l: 0.13, c: Math.min(p.c, 0.05), h: p.h });
  const surfaces = [surface, canvas, raised, sunken];
  const accent = adjustToContrast(secondary, canvas, AA_UI);
  const strong = legibleOnAll(secondary, surfaces, AA_TEXT);
  const cv = chromeVars(chrome, accent, tertiary, primary);
  // Textes secondaires : garantis sur toutes les surfaces sombres.
  n['--n-500'] = legibleOnAll(n['--n-500']!, surfaces, AA_TEXT);
  n['--n-600'] = legibleOnAll(n['--n-600']!, surfaces, AA_TEXT);
  Object.assign(vars, n, cv, {
    '--brand-accent': accent,
    '--brand-on-accent': inkOn(accent, [primary]),
    '--brand-strong': strong,
    '--brand-tertiary': tertiary ?? n['--n-300']!,
    '--masthead': chrome,
    '--on-masthead': cv['--on-chrome']!,
    '--on-masthead-2': cv['--on-chrome-2']!,
    '--masthead-line': tertiary ? mix(chrome, tertiary, 0.5) : accent,
    '--mark': accent,
    '--on-mark': inkOn(accent, [primary]),
  });
  if (tertiary) {
    vars['--line'] = mix(surface, tertiary, 0.2);
    vars['--line-strong'] = mix(surface, tertiary, 0.36);
  }
  return { mode, vars };
}

/** Toutes les variables que `applyBrand` peut poser (pour les retirer au changement de garage). */
const ALL_VARS = Array.from(
  new Set(
    BRAND_MODES.flatMap((m) =>
      Object.keys(
        brandTheme({ displayName: '', primaryColor: '#123456', secondaryColor: '#654321', tertiaryColor: '#888888', logoUrl: null, brandMode: m }).vars,
      ),
    ),
  ),
);

/** Attributs d'un conteneur qui porte un thème de marque (aperçus, miniatures). */
export function brandScopeProps(brand: Brand): { 'data-brand-mode': BrandMode; style: Record<string, string> } {
  // Couleurs pas encore choisies (console) : jetons neutres de la plateforme.
  if (!isHex(brand.primaryColor)) return { 'data-brand-mode': brandModeOf(brand.brandMode), style: {} };
  const t = brandTheme(isHex(brand.secondaryColor) ? brand : { ...brand, secondaryColor: brand.primaryColor });
  return { 'data-brand-mode': t.mode, style: t.vars };
}

/** Pose la marque sur `<html>` (ou la retire : `null` = présentation neutre de la plateforme). */
export function applyBrand(brand: Brand | null, root: HTMLElement = document.documentElement): void {
  for (const v of ALL_VARS) root.style.removeProperty(v);
  if (!brand) {
    root.removeAttribute('data-brand-mode');
    setThemeColor(null);
    return;
  }
  const t = brandTheme(brand);
  for (const [k, v] of Object.entries(t.vars)) root.style.setProperty(k, v);
  root.setAttribute('data-brand-mode', t.mode);
  setThemeColor(t.vars['--masthead'] ?? t.vars['--brand-primary'] ?? null);
  document.title = brand.displayName;
}

/** Couleur de la barre du navigateur (téléphone) : celle de l'en-tête. */
function setThemeColor(color: string | null): void {
  if (typeof document === 'undefined') return;
  let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (!color) {
    meta?.remove();
    return;
  }
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = 'theme-color';
    document.head.appendChild(meta);
  }
  meta.content = color;
}
